import {
    init,
    captureException,
    captureMessage,
    captureReactException,
    setUser,
    withScope,
    addBreadcrumb,
    replayIntegration,
    browserTracingIntegration,
} from '@sentry/react';
import logger from './logger';

class SentryManager {
    constructor() {
        this.initialized = false;
    }

    init() {
        if (this.initialized) {
            return;
        }
        const isProd = import.meta.env.REACT_APP_STAGE === 'prod';
        const isSandbox = import.meta.env.REACT_APP_STAGE === 'sandbox';
        // Only initialize in production or sandbox environments
        if (!isProd && !isSandbox) {
            return;
        }
        // Keep development API URL for testing
        const API_URL =
            import.meta.env.MODE !== 'development' ? `${window.location.origin}/` : import.meta.env.REACT_APP_API_URL;

        try {
            init({
                // Basic configuration
                dsn: import.meta.env.REACT_APP_SENTRY_DSN,
                environment: import.meta.env.REACT_APP_SENTRY_ENVIRONMENT,
                release: import.meta.env.REACT_APP_SENTRY_RELEASE,
                // Performance monitoring
                integrations: [
                    browserTracingIntegration({
                        tracePropagationTargets: [window.location.origin, `${API_URL}api`],
                    }),
                    replayIntegration({
                        maskAllText: false,
                        blockAllMedia: false,
                        networkDetailAllowUrls: [window.location.origin, `${API_URL}api`],
                    }),
                ],
                // Performance Sampling
                tracesSampleRate: isProd ? 0.1 : 0,
                replaysSessionSampleRate: 0,
                replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
                maxBreadcrumbs: 100,
                attachStacktrace: true,
                beforeSend(event) {
                    if (window.location.protocol === 'file:') {
                        return null;
                    }
                    if (event.exception) {
                        logger.error('[Sentry]', event.exception);
                    }
                    return event;
                },
                // Error filtering
                ignoreErrors: [
                    'NS_ERROR_STORAGE_IOERR',
                    'Non-Error promise rejection captured',
                    'ResizeObserver loop limit exceeded',
                    /^Network request failed$/,
                    'Server returned handshake error: Handshake was canceled',
                    'top.GLOBALS',
                    'fb_xd_fragment',
                    'Failed to fetch',
                    'Load failed',
                    'NetworkError',
                    'AbortError',
                ],
                denyUrls: [
                    // Facebook flakiness
                    /graph\.facebook\.com/i,
                    // Facebook blocked
                    /connect\.facebook\.net\/en_US\/all\.js/i,
                    // Chrome extensions
                    /extensions\//i,
                    /^chrome:\/\//i,
                    /^chrome-extension:\/\//i,
                ],
            });

            this.initialized = true;
            logger.success('Sentry initialized successfully');
        } catch (error) {
            logger.error('Failed to initialize Sentry:', error);
        }
    }

    /**
     * Captures and reports exceptions to Sentry
     * @param {Error} error - The error object to capture
     * @param {Object} context - Additional context for the error
     */
    captureException(error, context = {}, fingerprint) {
        if (!this.initialized) return;

        withScope(scope => {
            scope.setExtras(context);
            if (fingerprint) {
                scope.setFingerprint(fingerprint);
            }
            captureException(error);
        });
    }

    /**
     * Captures custom messages with specified severity levels
     * @param {string} message - Message to capture
     * @param {string} level - Severity level ('info', 'warning', 'error')
     * @param {Object} context - Additional context for the message
     */
    captureMessage(message, level = 'info', context = {}) {
        if (!this.initialized) return;

        withScope(scope => {
            scope.setLevel(level);
            scope.setExtras(context);
            captureMessage(message);
        });
    }

    captureUncaughtError(error, errorInfo) {
        if (!this.initialized || !(error instanceof Error)) return;
        captureReactException(error, errorInfo, {
            mechanism: { handled: false, type: 'auto.function.react.error_handler' },
        });
    }

    setUser(user) {
        if (!this.initialized) return;
        setUser({ id: user.id, email: user.email });
    }

    clearUser() {
        if (!this.initialized) return;
        setUser(null);
    }

    /**
     * Adds a breadcrumb to track user actions before errors
     * @param {string} category - Category of the breadcrumb (e.g., 'user-action', 'navigation', 'api')
     * @param {string} message - Description of the action
     * @param {Object} data - Additional data to include
     * @param {string} level - Severity level ('info', 'warning', 'error')
     */
    addBreadcrumb(category, message, data = {}, level = 'info') {
        if (!this.initialized) return;
        addBreadcrumb({
            category,
            message,
            data,
            level,
            timestamp: Date.now() / 1000,
        });
    }

    /**
     * Track user interactions (clicks, form submissions, etc.)
     * @param {string} action - Type of action (e.g., 'click', 'submit', 'open-modal')
     * @param {string} target - What was interacted with
     * @param {Object} data - Additional context
     */
    trackUserAction(action, target, data = {}) {
        this.addBreadcrumb('user-action', `${action}: ${target}`, data);
    }
}

export default new SentryManager();
